import {Component, OnInit, Output, EventEmitter, Input} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {UntypedFormGroup, UntypedFormControl, Validators, ValidatorFn, AbstractControl, FormControl, ValidationErrors, FormGroup} from '@angular/forms';
import {BackendService} from '../../common/services/backend.service';
import {InterviewDataService} from './services/interview-data.service';
import {TranslateService} from "@ngx-translate/core";
import {forkJoin} from 'rxjs';

@Component({
    selector: 'request-feedback',
    template: `
        <div class="modal-header">
            <h4 class="modal-title">{{'interviews.Request Feedback' | translate}}</h4>
            <button type="button" class="close" aria-label="Close" (click)="activeModal.dismiss('Cross click')">
                <span aria-hidden="true">&times;</span>
            </button>
        </div>
        <form [formGroup]="form" (submit)="requestFeedback()" #reqForm="ngForm">
            <div class="form modal-body">
                <div *ngIf="loading" class="text-center" style="padding: 2rem;">
                    <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
                    <span class="sr-only">{{'interviews.Loading...' | translate}}</span>
                </div>
                <div class="request-feedback-modal" *ngIf="!loading">
                <div *ngIf="formSubmitted && form.invalid" class="alert error">
                    <div *ngIf="form.controls.staff.invalid">{{'interviews.Staff Field Error' | translate }}</div>
                    <div *ngIf="form.controls.recipients.errors">
                        <div *ngIf="form.controls.recipients.invalid">{{'interviews.Email Validation' | translate}}</div>
                    </div>
                    <div *ngIf="form.errors?.['atLeastOneRequired']">{{'interviews.At least one type of review is required.' | translate}}</div>
                </div>
                <div *ngIf="hasAiMockRight && hasAiMockSetting && requestAiFeedbackBlurb" class="form-group field">
                    <label class="label field-label">
                        {{ 'interviews.Request AI Feedback Label' | translate }}
                    </label>
                    <div>
                        <input type="checkbox" formControlName="aiFeedback" (change)="resetFormSubmitted()" id="aiFeedback">
                        <label class="font_weight_normal" for="aiFeedback">
                            {{ 'interviews.Request AI Feedback' | translate }}
                        </label>
                    </div>
                    <p class="blurb text-gray text-sm padding-left-lg" [ngClass]="{'text-error': aiCredits === 1}">{{requestAiFeedbackBlurb}}</p>
                </div>
                <div class="form-group field" *ngIf="staffTypes.length">
                    <label class="label field-label">
                        {{ 'interviews.Send Requests for Feedback' | translate }}
                    </label>
                    <p class="blurb text-gray">{{'interviews.Send requests for feedback blurb' | translate}}</p>
                    <div *ngFor="let option of staffTypes">
                        <div>
                            <input
                                type="checkbox"
                                [value]="option"
                                (change)="onStaffTypeChange($event)"
                                [checked]="form.value.staffTypes.includes(option)"
                                [id]="'staffType_' + option"
                            >
                            <label class="font_weight_normal" [for]="'staffType_' + option">
                                {{ ('interviews.' + option) | translate }}
                            </label>
                        </div>
                    </div>
                </div>
                <div class="form-group field" *ngIf="!hideAdvisorPicklist" [ngClass]="{'field-error' : form.controls.staff.invalid && formSubmitted}">
                    <label for="staff" class="label field-label">
                        {{'interviews.From Staff' | translate}}
                        <span *ngIf="advisorRequired" class="req-indicator">*</span>
                    </label>
                    <p class="blurb">{{'interviews.From Staff Blurb' | translate}}</p>
                    <div class="error-message" *ngIf="form.controls.staff.invalid && formSubmitted">
                        <i class="icn-error_filled"></i>
                        <span class="error-message">{{'interviews.Staff Field Error' | translate }}</span>
                    </div>
                    <select class="field-widget" name="from-staff" id="staff" formControlName="staff">
                        <option [ngValue]=""></option>
                        <option *ngFor="let staff of selectedAdvisors" [ngValue]="staff" id="{{staff.id}}">
                            {{ staff.advisor }}
                        </option>
                    </select>
                </div>
                <div class="form-group field" *ngIf="!hideOtherRecipients" [ngClass]="{'field-error' : form.controls.recipients.invalid && formSubmitted}">
                    <label for="recipients" class="label field-label">
                        {{'interviews.Other Recipients' | translate}}
                        <span *ngIf="otherRecipientsRequired" class="req-indicator">*</span>
                    </label>
                    <p class="blurb">{{'interviews.Other Recipients Blurb' | translate}}</p>
                    <div class="error-message" *ngIf="form.controls.recipients.invalid && formSubmitted">
                        <i class="icn-error_filled"></i>
                        <span class="error-message">{{'interviews.Email Validation' | translate}}</span>
                    </div>
                    <input type="email" class="field-widget" id="recipients" name="recipients" formControlName="recipients">
                </div>
                </div>
            </div>
            <div class="modal-footer" *ngIf="!loading">
                <button type="button" class="btn btn_secondary" (click)="activeModal.close('Close click')">{{'interviews.Close' | translate}}</button>
                <button type="submit" class="btn btn_primary">{{'interviews.Submit' | translate}}</button>
            </div>
        </form>
    `,
    standalone: false
})
export class RequestFeedbackComponent implements OnInit {
    form: UntypedFormGroup;
    formSubmitted: boolean = false;
    loading: boolean = true;
    aiCredits: number = 0;
    selectedAdvisors: any;
    hideAdvisorPicklist: boolean = true;
    hideOtherRecipients: boolean = true;
    advisorRequired: boolean = false;
    otherRecipientsRequired: boolean = false;
    staffTypes: string[] = [];
    hasAiMockRight: boolean = false;
    hasAiMockSetting: boolean = false;
    requestAiFeedbackBlurb: string = '';
    questions: any[] = [];

    @Output() feedbackReq: EventEmitter<any> = new EventEmitter<any>();

    constructor(
        public activeModal: NgbActiveModal,
        private _dataService: InterviewDataService,
        private backendService: BackendService,
        private translate: TranslateService
    ) { }

    ngOnInit() {
        this.form = new UntypedFormGroup({
            aiFeedback: new UntypedFormControl(false),
            staffTypes: new UntypedFormControl([]),
            staff: new UntypedFormControl(this.selectedAdvisors, { updateOn: 'submit' }),
            recipients: new UntypedFormControl(null, { updateOn: 'submit' })
        }, { validators: this.atLeastOneFieldValidator });

        this.setupAdvisorPicklist();
        this.setupStaffTypes();

        forkJoin({
            rights: this.backendService.getOne('use_ai_mock_interview_feedback', 'auth/check-rights'),
            credits: this.backendService.getAll('', 'video-interview/' + this._dataService.interviewId + '/student-ai-usage')
        }).subscribe(({ rights, credits }) => {
            this.hasAiMockRight = rights.data;
            this.checkAiFeedbackState();
            if (!this.form.get('aiFeedback').disabled) {
                this.loadAiCredits(credits);
            }
            this.loading = false;
        });
    }

    atLeastOneFieldValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
        const group = control as FormGroup;
        const hasValue = Object.keys(group.controls).some(key => {
            const value = group.get(key)?.value;
            if (Array.isArray(value)) {
                return value.length;
            }
            return !!value;
        });
        return hasValue ? null : { atLeastOneRequired: true };
    };

    requestFeedback() {
        this.formSubmitted = true;

        if (this.form.invalid) {
            return;
        }

        let data = <any>{};
        let aiFeedback = this.form.controls['aiFeedback'].value;
        let staff = this.form.controls['staff'].value;
        let staffTypes = this.form.controls['staffTypes'].value;
        let recipients = this.form.controls['recipients'].value;

        data.ai_feedback = aiFeedback;
        data.staff_type = staffTypes;
        data.recipients = recipients;

        if (staff) {
            data.selected_advisors = staff.id;
            data.from_staff = true;
        } else {
            data.from_staff = false;
        }

        if (aiFeedback) {
            this._dataService.aiFeedbackRequested = true;
        }

        this.feedbackReq.emit(data);
    }

    setupAdvisorPicklist() {
        let advisorsList = [];
        for (let key in this._dataService.reviewerStaff) {
            let obj = <any>{};
            obj.id = key;
            obj.advisor = this._dataService.reviewerStaff[key];
            advisorsList.push(obj);
        }
        this.selectedAdvisors = advisorsList;
    }

    setupStaffTypes() {
        const map = {
            'Assigned counselor': 'assigned_counselor',
            'Assigned faculty': 'assigned_faculty',
            'General career services staff': 'general_career',
            'Selected career services staff': 'specific_career',
            'External user': 'others'
        };
        for (const reviewerType in map) {
            if (this._dataService.reviewerTypes.includes(reviewerType)) {
                this.staffTypes.push(map[reviewerType]);
            }
        }
        this.hasAiMockSetting = this._dataService.reviewerTypes.includes('AI feedback');
    }

    checkAiFeedbackState() {
        const control = this.form.get('aiFeedback');

        if (this._dataService.aiFeedbackRequested) {
            this.requestAiFeedbackBlurb = this.translate.instant('interviews.AI Feedback Processing');
            control.disable();
            return;
        }

        if (!this.questions?.length) {
            return;
        }
        const aiFeedbacks = this.questions
            .flatMap(q => q.feedback || [])
            .filter(f => f.reviewer_type === 'AI');

        if (aiFeedbacks.length) {
            const hasProcessing = aiFeedbacks.some(f => ['pending', 'queued', 'started'].includes(f.status));
            const allCompleted = aiFeedbacks.every(f => f.status === 'completed');

            if (hasProcessing) {
                this.requestAiFeedbackBlurb = this.translate.instant('interviews.AI Feedback Processing');
                control.disable();
            } else if (allCompleted) {
                this.requestAiFeedbackBlurb = this.translate.instant('interviews.AI Feedback Already Requested');
                control.disable();
            }
        }

        if (control.disabled) {
            return;
        }

        if (this.questions.length > 15) {
            this.requestAiFeedbackBlurb = this.translate.instant('interviews.AI Feedback Max Questions Exceeded');
            control.disable();
        }
    }

    loadAiCredits(res: any) {
        const control = this.form.get('aiFeedback');
        if (!res.schoolHasCredits) {
            this.requestAiFeedbackBlurb = this.translate.instant('interviews.No School AI Credits');
            control.disable();
        } else if (res.credits < 1) {
            this.requestAiFeedbackBlurb = this.translate.instant('interviews.Max AI Used');
            control.disable();
        } else {
            this.requestAiFeedbackBlurb = this.translate.instant('interviews.{count} uses of AI feedback left this academic year', { count: res.credits });
            this.aiCredits = res.credits;
        }
    }

    updateHideAdvisorPicklist() {
        const hasSelectedStaffSetting = this._dataService.reviewerTypes.length && this._dataService.reviewerTypes.includes('Selected career services staff');
        const selectedStaffChecked = this.isStaffTypeChecked('specific_career');
        if (!hasSelectedStaffSetting || !selectedStaffChecked) {
            this.hideAdvisorPicklist = true;
        } else {
            this.hideAdvisorPicklist = false;
        }
    }

    updateStaffValidators() {
        const selectedStaffChecked = this.isStaffTypeChecked('specific_career');
        const control = this.form.get('staff');
        if (selectedStaffChecked) {
            this.advisorRequired = true;
            control.setValidators([Validators.required]);
        } else {
            this.advisorRequired = false;
            control.clearValidators();
            control.reset();
        }
        control.updateValueAndValidity();
    }

    updateOtherRecipientsValidators() {
        const multiEmailValidator: ValidatorFn = (control: AbstractControl<string>) => {
            if (!control.value) {
                return null;
            }
            const isEmailValid = (email: string) => Validators.email(new FormControl(email)) === null;

            const emails = control.value.split(',').map((email) => email.trim());
            if (emails.length > 1 && !emails[emails.length - 1]) {
                return { email: true };
            }
            if (emails.every(isEmailValid)) {
                return null;
            }
            return { email: true };
        }

        const othersChecked = this.isStaffTypeChecked('others');
        if (othersChecked) {
            this.otherRecipientsRequired = true;
            this.form.controls['recipients'].setValidators([Validators.required, multiEmailValidator]);
        } else {
            this.otherRecipientsRequired = false;
            this.form.controls['recipients'].setValidators([multiEmailValidator]);
        }
        this.form.controls['recipients'].updateValueAndValidity();
    }

    isStaffTypeChecked(type: string) {
        return this.form.controls['staffTypes'].value.includes(type);
    }

    onStaffTypeChange(event: Event) {
        this.resetFormSubmitted();

        const checkbox = event.target as HTMLInputElement;
        const control = this.form.get('staffTypes');
        const selected = control.value as string[];

        if (checkbox.checked) {
            control.setValue([...selected, checkbox.value]);
        } else {
            control.setValue(
                selected.filter(v => v !== checkbox.value)
            );
        }

        this.updateStaffValidators();
        this.updateOtherRecipientsValidators();
        this.updateHideAdvisorPicklist();
        this.hideOtherRecipients = !this.isStaffTypeChecked('others');
    }

    resetFormSubmitted() {
        this.formSubmitted = false;
    }

}
